import React, { useEffect, useMemo, useRef, useState } from "react";
import { SearchableSelect as SelectM } from "@/lib/shadcn/components/searchable-select";
import { ApartmentStats, Convert } from "@/models/responses";
import { overViewIncomeFormatter } from "@/utils/formatters";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader } from "@/lib/shadcn/ui/card";
import ApartmentMetricChart from "@/pages/graphs/apartment-metric-chart";
import ApartmentYearOverYearChart from "@/pages/graphs/apartments-year-over-year-chart";
import { MultiSelect } from "@/lib/shadcn/components/multi-select";
import { useActiveApartmentEntities, useActiveApartmentOptions } from "@/store/slices/apartments";
import { DatePicker } from "@/lib/shadcn/components/date-picker";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { useAppData } from "@/hooks/useAppData";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useFilteredApartmentIds } from "@/hooks/useFilteredApartmentIds";

interface GraphData {
    date: string;

    [key: string]: string | number;
}

export type Metric = "grossIncome" | "netIncome" | "occupancy" | "numberOfVisits" | "grossAdr" | "netAdr" | "averageBlt" | "averageLengthOfStay";

const metricOptions: {
    label: string;
    value: Metric;
    formatter: (value: number) => string;
}[] = [
    {
        label: "Gross Income",
        value: "grossIncome",
        formatter: overViewIncomeFormatter,
    },
    {
        label: "Net Income",
        value: "netIncome",
        formatter: overViewIncomeFormatter,
    },
    {
        label: "Gross ADR",
        value: "grossAdr",
        formatter: overViewIncomeFormatter,
    },
    { label: "Net ADR", value: "netAdr", formatter: overViewIncomeFormatter },
    {
        label: "Average BLT",
        value: "averageBlt",
        formatter: (value: number) => value.toFixed(1),
    },
    {
        label: "Average Length of Stay",
        value: "averageLengthOfStay",
        formatter: (value: number) => value.toFixed(1),
    },
    {
        label: "Occupancy",
        value: "occupancy",
        formatter: (value: number) => value.toFixed(0) + "%",
    },
    {
        label: "Number of Visits",
        value: "numberOfVisits",
        formatter: (value: number) => value.toFixed(0),
    },
];

const monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Traceability: FR/EC IDs reference
// .verified/features/apartment-filters-graphs-and-database/spec.md. The four
// filters narrow the apartment MultiSelect; non-matching selections
// auto-deselect (FR-006).
export const ApartmentGraphsPage: React.FC = () => {
    const [selectedMetric, setSelectedMetric] = React.useState(metricOptions[0]);
    const now = new Date();
    const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    const [startDate, setStartDate] = useState<Date>(oneYearAgo);
    const [endDate, setEndDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 1));
    const [apartmentStats, setApartmentStats] = useState<ApartmentStats[]>([]);
    const [months, setMonths] = useState<Date[]>([]);
    const [numberOfApartments, setNumberOfApartments] = useState(0);
    const [selectedApartments, setSelectedApartments] = useState<string[]>([]);
    const [graphData, setGraphData] = useState<GraphData[]>([]);
    const apartmentEntities = useActiveApartmentEntities();
    const apartmentOptions = useActiveApartmentOptions();
    const { user } = useAuth();
    const { selectedApartment } = useAppData();
    const filteredApartmentIds = useFilteredApartmentIds();

    // Apartment options narrowed to the active filter; `filteredApartmentIds
    // === null` means no filter → full list. ("All"/value "0" is dropped
    // elsewhere.)
    const filteredApartmentOptions = useMemo(() => {
        if (filteredApartmentIds === null) {
            return apartmentOptions;
        }
        return apartmentOptions.filter((opt) => opt.value === "0" || filteredApartmentIds.includes(Number(opt.value)));
    }, [apartmentOptions, filteredApartmentIds]);

    // ONE-SHOT init of selected apartments from Redux, first mount only. Must
    // NOT re-fire on later empties: re-init races the auto-deselect effect
    // (narrow → empty → re-init picks one → auto-deselect strips it → infinite
    // loop, EC-003) and would override the spec'd empty-state (leave the chart
    // empty, don't silently re-pick).
    const hasInitialised = useRef(false);
    useEffect(() => {
        if (hasInitialised.current) {
            return;
        }
        if (apartmentOptions && apartmentOptions.length > 1) {
            const validOptions = apartmentOptions.filter((opt) => opt.value !== "0");
            if (validOptions.length > 0) {
                const persisted = validOptions.find((opt) => opt.value === selectedApartment.toString());
                const selected = persisted || validOptions[0];
                if (selected?.value) {
                    // eslint-disable-next-line react-hooks/set-state-in-effect
                    setSelectedApartments([selected.value]);
                    hasInitialised.current = true;
                }
            }
        }
    }, [apartmentOptions, selectedApartment]);

    // FR-006 auto-deselect: when the filter narrows the option list, drop
    // previously-selected apartments no longer in the matching set. The
    // fetch effect keys on `selectedApartments`, so it re-fires naturally.
    // Guarded by `!== null` so unfiltered renders don't touch the selection;
    // the pre-filter selection is not cached (users re-select on clear).
    useEffect(() => {
        if (filteredApartmentIds === null) {
            return;
        }
        const allowed = new Set(filteredApartmentIds.map((id) => id.toString()));
        const next = selectedApartments.filter((id) => allowed.has(id));
        if (next.length !== selectedApartments.length) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedApartments(next);
        }
    }, [filteredApartmentIds, selectedApartments]);

    // Fetch data when dates change
    useEffect(() => {
        if (!selectedApartments?.length || !user) {
            // No selection → clear stale stats so the chart renders empty, not
            // the previous frame (EC-003). Only fires on the empty-selection
            // branch, so it doesn't loop.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setApartmentStats([]);
            setGraphData([]);
            return;
        }

        const apartmentStatsUrl = `${apiUrl}${getApiBasePath()}/${user.isAdmin ? "admin/" : ""}apartments/stats-by-month?apartments=${selectedApartments.join(",")}&startDate=${startDate.toISOString().slice(0, 10)}&endDate=${endDate.toISOString().slice(0, 10)}`;

        fetch(apartmentStatsUrl, fetchOptions)
            .then((response) => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.text();
            })
            .then((data) => {
                const stats = Convert.toGetMonthlyApartmentStatsResponse(data);
                setApartmentStats(stats.apartmentStats);
                setMonths(stats.months);
                setNumberOfApartments(stats.numberOfApartments);
            })
            .catch((error) => {
                console.error("Error fetching apartment stats:", error);
                setApartmentStats([]);
            });
    }, [startDate, endDate, selectedApartments, user]);

    // Transform apartment stats into graph data
    useEffect(() => {
        if (apartmentOptions === undefined || !apartmentStats?.length || apartmentOptions.length === 0 || !selectedMetric) return;

        const statsByDate = new Map<string, Record<string, number>>();

        apartmentStats.forEach((apartmentStat, index) => {
            const date = months[Math.floor(index / numberOfApartments)];
            const dateStr = `${monthNamesShort[date.getMonth()]} '${date.getFullYear().toString().substr(-2)}`;
            const apartmentName = apartmentEntities[apartmentStat.apartmentId]?.name || "Unknown";

            if (!statsByDate.has(dateStr)) {
                statsByDate.set(dateStr, {});
            }

            const stats = statsByDate.get(dateStr)!;
            stats[apartmentName] = apartmentStat[selectedMetric.value];
        });

        const transformedData = Array.from(statsByDate.entries()).map(([date, values]) => ({
            date,
            ...values,
        }));

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setGraphData(transformedData);
    }, [apartmentStats, selectedMetric, apartmentEntities, apartmentOptions, months, numberOfApartments]);

    const onMetricChanged = (val: string) => {
        if (!val) return;
        const newMetric = metricOptions.find((option) => option.value === val);
        if (newMetric) setSelectedMetric(newMetric);
    };

    // Wait for essential data to be loaded
    if (apartmentOptions === undefined || apartmentOptions.length == 1 || !selectedMetric) {
        return <div>Loading...</div>; // Instead of return null
    }

    return (
        <>
            <Card className="shadow-sm max-w-[92vw] overflow-x-auto" data-testid="apartment-graphs-page">
                <CardHeader className="overflow-visible">
                    <ApartmentFilterBar />
                    <div className="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-2 sm:gap-4">
                        <DatePicker onChange={setStartDate} selectedValue={startDate} variant={"volt"} />
                        <DatePicker onChange={setEndDate} selectedValue={endDate} variant={"volt"} />
                        <SelectM
                            variant="volt"
                            onChange={onMetricChanged}
                            placeholder="Metric"
                            selectedValue={selectedMetric?.value}
                            options={metricOptions}
                            className="w-auto"
                            selectedValuePrefix="Metric"
                        />
                        <div data-testid="apartment-graphs-multiselect">
                            <MultiSelect
                                variant="volt"
                                staticText="Selected Apartments"
                                options={filteredApartmentOptions
                                    .filter((option) => option?.value !== "0")
                                    .sort((a, b) => a.label.localeCompare(b.label))}
                                onChange={setSelectedApartments}
                                selectedValues={selectedApartments}
                                placeholder="Select Apartments"
                                className="sm:w-auto"
                            />
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="flex flex-col space-y-6 px-6">
                    <div data-testid="apartment-graphs-chart">
                        <ApartmentMetricChart data={graphData} metric={selectedMetric?.label} formatter={selectedMetric?.formatter} />
                    </div>
                </CardContent>
            </Card>

            {selectedMetric && (
                <Card className="mt-6">
                    <CardContent>
                        <ApartmentYearOverYearChart
                            selectedMetric={selectedMetric}
                            selectedApartmentId={selectedApartments[0]}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    </CardContent>
                </Card>
            )}
        </>
    );
};
